import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import Header from "./Header";
import Notification from "./Notification";
import { FiEdit, FiTrash2, FiChevronRight, FiX, FiPlus } from "react-icons/fi";
import { FaUsers } from "react-icons/fa";
import { Link } from "react-router-dom";
import axios from "axios";

const GroupManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 1024 : true));
  const [groups, setGroups] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isEditGroupModalOpen, setIsEditGroupModalOpen] = useState(false);
  const [isDeleteGroupModalOpen, setIsDeleteGroupModalOpen] = useState(false);
  const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [editGroup, setEditGroup] = useState({ group_id: "", group_name: "", company_id: "" });
  const [newGroup, setNewGroup] = useState({ group_name: "", company_id: "" });
  const user = JSON.parse(localStorage.getItem("user"));
  const role = user ? user.role : null;

  // Pindahkan ke luar useEffect
  const fetchGroupsAndCustomers = async () => {
    setIsLoading(true);
    try {
      const companyId = user ? user.company_id : null;
      if (companyId) {
        const groupResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/groups/company/${companyId}`);
        setGroups(groupResponse.data);
      }
      const customerResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/customers/`);
      setCustomers(customerResponse.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupsAndCustomers();
  }, []);

  const closeNotification = () => setNotification(null);
  const handleEditGroup = (group) => {
    setEditGroup({ group_id: group.group_id, group_name: group.group_name, company_id: group.company_id });
    setIsEditGroupModalOpen(true);
  };
  const handleEditGroupChange = (e) => {
    const { name, value } = e.target;
    setEditGroup((prevState) => ({ ...prevState, [name]: value }));
  };
  const handleEditGroupSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/groups/${editGroup.group_id}`, {
        group_name: editGroup.group_name,
        company_id: editGroup.company_id,
      });
      setNotification({ type: "success", message: "Group updated successfully!" });
      setGroups(groups.map((group) => (group.group_id === editGroup.group_id ? { ...group, group_name: editGroup.group_name } : group)));
      setIsEditGroupModalOpen(false);
    } catch (error) {
      setNotification({ type: "error", message: "Failed to update group. Please try again." });
    }
  };
  const handleDeleteGroup = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/groups/${groupToDelete}`);
      setNotification({ type: "success", message: "Group deleted successfully!" });
      setGroups(groups.filter((group) => group.group_id !== groupToDelete));
      setIsDeleteGroupModalOpen(false);
    } catch (error) {
      setNotification({ type: "error", message: "Failed to delete group. Please try again." });
    }
  };
  const openDeleteGroupModal = (group_id) => {
    setGroupToDelete(group_id);
    setIsDeleteGroupModalOpen(true);
  };
  const handleAddGroupChange = (e) => {
    const { name, value } = e.target;
    setNewGroup((prevState) => ({ ...prevState, [name]: value }));
  };
  const handleAddGroupSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/groups/`, newGroup);
      setNotification({ type: "success", message: "Group created successfully!" });
      setIsAddGroupModalOpen(false);
      // Fetch ulang data group agar langsung muncul
      fetchGroupsAndCustomers();
    } catch (error) {
      setNotification({ type: "error", message: "Failed to create group. Please try again." });
    }
  };
  const openAddGroupModal = () => {
    setNewGroup({ group_name: "", company_id: user ? user.company_id : "" });
    setIsAddGroupModalOpen(true);
  };
  const getCompanyName = (companyId) => {
    const company = customers.find((customer) => customer.company_id === companyId);
    return company ? company.company_name : "Unknown Company";
  };
  const toggleSidebar = () => setSidebarOpen((prevState) => !prevState);
  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
        <div className="flex flex-1 bg-gray-100 min-w-0 max-w-full overflow-x-hidden">
          <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
          <div className={`flex-1 min-w-0 max-w-full overflow-x-hidden p-3 sm:p-6 transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"} ml-0`}>
            <div className="pt-20 pb-6">
              <div className="mb-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 sm:px-6 py-4 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                      <div className="flex items-center">
                        <div className="bg-green-100 p-2 rounded-lg mr-3">
                          <FaUsers className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h2 className="text-xl font-semibold text-gray-900">Group Management</h2>
                          <p className="text-sm text-gray-600">Organize users into groups for better access control</p>
                        </div>
                      </div>
                      <button className="flex items-center justify-center bg-green-600 text-white py-2 px-4 rounded-lg shadow-sm" disabled>
                        <FiPlus className="mr-2 w-4 h-4" />
                        Add Group
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto w-full">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Group Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                    </table>
                    <div className="bg-white">
                      <div className="divide-y divide-gray-200">
                        {[...Array(5)].map((_, index) => (
                          <div key={index} className="flex">
                            <div className="px-6 py-4 w-1/3">
                              <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
                            </div>
                            <div className="px-6 py-4 w-1/3">
                              <div className="h-4 bg-gray-200 rounded animate-pulse w-2/3"></div>
                            </div>
                            <div className="px-6 py-4 w-1/3 flex justify-center">
                              <div className="h-8 bg-gray-200 rounded animate-pulse w-16"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col min-h-screen">
      <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
      <div className="flex flex-1 bg-gray-100 min-w-0 max-w-full overflow-x-hidden">
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className={`flex-1 min-w-0 max-w-full overflow-x-hidden p-3 sm:p-6 transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"} ml-0`}>
          <div className="pt-20 pb-6">
            <div className="mb-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 sm:px-6 py-4 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div className="flex items-center">
                      <div className="bg-green-100 p-2 rounded-lg mr-3">
                        <FaUsers className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">Group Management</h2>
                        <p className="text-sm text-gray-600">Organize users into groups for better access control</p>
                      </div>
                    </div>
                    <button onClick={openAddGroupModal} className="flex items-center justify-center bg-green-600 text-white py-2 px-4 rounded-lg shadow-sm hover:bg-green-700 transition-all duration-200 hover:shadow-md"><FiPlus className="mr-2 w-4 h-4" />Add Group</button>
                  </div>
                </div>
                <div className="overflow-x-auto w-full">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Group Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {groups.map((group) => (
                        <tr key={group.group_id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link to={`/groups/${group.group_id}`} className="flex items-center text-blue-600 hover:text-blue-800 font-medium"><FaUsers className="w-4 h-4 mr-2" />{group.group_name}<FiChevronRight className="w-4 h-4 ml-1" /></Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{getCompanyName(group.company_id)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex justify-center space-x-2">
                              <button className="text-blue-600 hover:text-blue-800 p-1 rounded-md hover:bg-blue-50 transition-colors" onClick={() => handleEditGroup(group)}><FiEdit className="w-4 h-4" /></button>
                              <button className="text-red-600 hover:text-red-800 p-1 rounded-md hover:bg-red-50 transition-colors" onClick={() => openDeleteGroupModal(group.group_id)}><FiTrash2 className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {notification && (<Notification type={notification.type} message={notification.message} onClose={closeNotification} />)}
      {/* Edit, Delete, Add Group Modals (same as before, copy from AllUsers.jsx) */}
      {/* ...modals code... */}
      {/* Create Group Modal */}
      {isAddGroupModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Create New Group</h3>
              <button
                onClick={() => setIsAddGroupModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleAddGroupSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Group Name</label>
                <input
                  type="text"
                  name="group_name"
                  value={newGroup.group_name}
                  onChange={handleAddGroupChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company ID</label>
                <input
                  type="text"
                  name="company_id"
                  value={newGroup.company_id}
                  readOnly
                  className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsAddGroupModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Create Group
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Edit Group Modal */}
      {isEditGroupModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Edit Group</h3>
              <button
                onClick={() => setIsEditGroupModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleEditGroupSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Group Name</label>
                <input
                  type="text"
                  name="group_name"
                  value={editGroup.group_name}
                  onChange={handleEditGroupChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsEditGroupModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Delete Group Modal */}
      {isDeleteGroupModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <FiTrash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Group</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this group? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setIsDeleteGroupModalOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteGroup}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Group
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupManagement;